import { Module } from 'vuex';
import { CategoryTyping } from '@/modules/tickets/shared/domain/category-typing';
import { Contact } from '@/modules/contacts/shared/domain/contact';
import { Typing, TypingLevels } from '@/modules/tickets/shared/domain/typing';
import { User } from '@/modules/users/domain/user';
import { UserGetters } from '@/modules/users/store/user.getters';
import ticketsRepository from '@/modules/tickets/shared/repositories/tickets.repository';
import { Tickets } from '@/modules/shared/shared/domain/constants/constants';
import { Exception } from '@/modules/shared/shared/domain/exceptions/exception';
import { EventBus } from '@/main';
import { EventBusMessages } from '@/modules/shared/shared/domain/constants/event-bus-messages';
import { NetworkSecurityInternetIncidentState } from '@/modules/tickets/incidents/network-security-internet/store/network-security-internet-incident.state';
import { NetworkSecurityInternetIncidentGetters } from '@/modules/tickets/incidents/network-security-internet/store/network-security-internet-incident.getters';
import { NetworkSecurityInternetIncidentData } from '@/modules/tickets/incidents/network-security-internet/domain/network-security-internet-incident-data';
import { NetworkSecurityInternetIncidentMutations } from '@/modules/tickets/incidents/network-security-internet/store/network-security-internet-incident.mutations';
import { NetworkSecurityInternetActions } from '@/modules/tickets/requirement/network-security-internet/store/network-security-internet.actions';
import { NetworkSecurityInternetIncidentTicket } from '@/modules/tickets/incidents/network-security-internet/domain/network-security-internet-incident-ticket';
import incidentsRepository from '@/modules/tickets/shared/repositories/incidents.repository';
import { AttachedFile } from '@/modules/tickets/incidents/shared/domain/attached-file';

const networkSecurityInternetIncident: Module<NetworkSecurityInternetIncidentState, any> = {
    namespaced: true,
    state: {
        formValues: {
            generalInformation: {
                subCategory: null,
                subject: null,
            },
            categoriesTypings: [],
        },
        incidents: [],
    },
    getters: {
        [NetworkSecurityInternetIncidentGetters.GET_FORM_VALUES](
            state,
        ): {
            generalInformation: NetworkSecurityInternetIncidentData;
            categoriesTypings: CategoryTyping;
        } {
            return state.formValues;
        },
        [NetworkSecurityInternetIncidentGetters.GET_OPEN_INCIDENTS](state) {
            return state.incidents;
        },
    },
    mutations: {
        [NetworkSecurityInternetIncidentMutations.SET_FORM_VALUES](state, formValues) {
            state.formValues = formValues;
        },
        [NetworkSecurityInternetIncidentMutations.SET_INCIDENTS](state, incidents) {
            state.incidents = incidents;
        },
        [NetworkSecurityInternetIncidentMutations.SET_CONTACT_INFORMATION](
            state,
            contact: Contact,
        ) {
            state.formValues.generalInformation.firstName = contact.name;
            state.formValues.generalInformation.lastName = contact.lastName;
            state.formValues.generalInformation.contactId = contact.id;
            state.formValues.generalInformation.idNumber = contact.ci;
            state.formValues.generalInformation.telephoneNumber = contact.phone;
        },
        [NetworkSecurityInternetIncidentMutations.SET_TYPINGS](state, typings: Typing[]) {
            state.formValues.categoriesTypings = typings.filter(
                typing => typing.level === TypingLevels.CATEGORY_LEVEL,
            );

            const subjects = typings.filter(typing => typing.level === TypingLevels.SUBJECT_LEVEL);
            const subCategories = typings.filter(
                typing => typing.level === TypingLevels.SUBCATEGORY_LEVEL,
            );

            state.formValues.categoriesTypings = state.formValues.categoriesTypings.map(
                category => {
                    return {
                        ...category,
                        subCategoriesTypings: subCategories
                            .filter(subCategory => subCategory.typing === category.id)
                            .map(subCategory => {
                                return {
                                    ...subCategory,
                                    subjectTypings: subjects.filter(
                                        subject => subject.typing === subCategory.id,
                                    ),
                                };
                            }),
                    };
                },
            );
        },
    },
    actions: {
        async [NetworkSecurityInternetActions.FETCH_VIEW]({
            commit,
            rootGetters,
        }): Promise<RequestResponse> {
            commit(NetworkSecurityInternetIncidentMutations.SET_FORM_VALUES, {
                generalInformation: {
                    subCategory: null,
                    subject: null,
                },
                categoriesTypings: [],
            });
            commit(NetworkSecurityInternetIncidentMutations.SET_INCIDENTS, []);
            const currentUser: User = rootGetters[`user/${UserGetters.GET_CURRENT_USER}`];
            commit(
                NetworkSecurityInternetIncidentMutations.SET_CONTACT_INFORMATION,
                currentUser.contact,
            );
            try {
                const typings = await ticketsRepository.getTypings(
                    currentUser.contact.customer.id,
                    Tickets.NetworkSecurityAndInternetIncident.name,
                );
                commit(NetworkSecurityInternetIncidentMutations.SET_TYPINGS, typings);
                const incidents = await incidentsRepository.getOpenIncidents(
                    currentUser.contact.customer.id,
                    Tickets.NetworkSecurityAndInternetIncident.name,
                );
                commit(NetworkSecurityInternetIncidentMutations.SET_INCIDENTS, incidents);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [NetworkSecurityInternetActions.CREATE_TICKET](
            { rootGetters },
            information: NetworkSecurityInternetIncidentData,
        ): Promise<RequestResponse> {
            try {
                const currentUser: User = rootGetters[`user/${UserGetters.GET_CURRENT_USER}`];
                const contact = currentUser.contact;
                const customer = contact.customer;
                const ticket: NetworkSecurityInternetIncidentTicket = {
                    description: information.description || '',
                    typing: information.subject!,
                    ticketType: Tickets.NetworkSecurityAndInternetIncident,
                    affectedLocation: information.affectedLocation!,
                    affectedService: information.affectedService!,
                    attachments: [{ fileName: information.attachment!.name }],
                    customer: {
                        id: customer.id,
                        name: customer.name,
                        category: customer.category!,
                        contact: {
                            id: contact.id,
                            name: contact.name,
                            lastName: contact.lastName,
                            email: contact.email,
                            ci: contact.ci!,
                        },
                    },
                };

                const createdTicket: { id: number; attachments: AttachedFile[] } = (
                    await ticketsRepository.createTicket(ticket)
                ).data;

                const attachment = createdTicket.attachments[0];
                const formData = new FormData();
                formData.set(
                    'file',
                    information.attachment!,
                    attachment.fileName + attachment.extension,
                );
                formData.set('mailAttachmentId', '' + attachment.mailAttachmentId);
                formData.set('ticketAttachmentId', '' + attachment.ticketAttachmentId);
                await incidentsRepository.uploadIncidentAttachment(createdTicket.id, formData);

                EventBus.$emit(
                    EventBusMessages.SHOW_SUCCESS,
                    `Su ticket fue creado exitosamente (Serial Nº ${createdTicket.id})`,
                );
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },
    },
};

export default networkSecurityInternetIncident;
